import { assetUrl } from "@/i18n/assets";
import { Link } from "@/i18n/Link";
import { useState } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd, blogJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { getPosts, postImage } from "@/blog/posts";
import { postPrefetchHandlers, useIdlePrefetchPosts } from "@/blog/use-prefetch";
import { useCopy, useLocale } from "@/i18n/LocaleContext";

const CATEGORIES = ["All", "Axles & Suspension", "Service & Repair", "Buyer Guides", "Trailer Parts"] as const;

const COPY = {
  en: {
    trail: [{ name: "Home", path: "/" }, { name: "Blog", path: "/blog/" }],
    seoTitle: "Trailer Maintenance & Repair Blog - Elkhart, IN | Axle Inc.",
    seoDesc: "Practical trailer maintenance guides, axle how-tos, and parts picks from the Axle Inc. service team in Elkhart, IN. Written for northern Indiana trailer owners.",
    blogName: "Axle Inc. - Trailer Parts & Service Blog",
    blogDesc: "Practical guides, how-tos, and product picks for trailer owners.",
    subTitle: "From the shop",
    title: "Practical guides & how-tos.",
    description: "What we'd tell a customer in person, written down. Buying guides, repair walk-throughs, and the maintenance work most people skip.",
    searchLabel: "Search the blog",
    searchPlaceholder: "Search posts - try 'wheel bearings', 'electric brakes', 'boat trailer'…",
    clearSearch: "Clear search",
    categories: { All: "All", "Axles & Suspension": "Axles & Suspension", "Service & Repair": "Service & Repair", "Buyer Guides": "Buyer Guides", "Trailer Parts": "Trailer Parts" },
    noPosts: (query: string, active: string) => `No posts match "${query}"${active !== "All" ? ` in ${active}` : ""}.`,
    clearFilters: "Clear filters",
    readMore: "Read more",
    showing: (shown: number, total: number) => `Showing ${shown} of ${total} posts`,
    showMore: (n: number) => `Show ${n} more`,
    showAll: (n: number) => `Show all ${n}`
  },
  es: {
    trail: [{ name: "Inicio", path: "/" }, { name: "Blog", path: "/blog/" }],
    seoTitle: "Blog de Mantenimiento y Reparación de Remolques - Elkhart, IN | Axle Inc.",
    seoDesc: "Guías prácticas de mantenimiento de remolques, tutoriales sobre ejes y selección de piezas del equipo de servicio de Axle Inc. en Elkhart, IN. Escrito para propietarios de remolques del norte de Indiana.",
    blogName: "Axle Inc. - Blog de Piezas y Servicio para Remolques",
    blogDesc: "Guías prácticas, tutoriales y selecciones de productos para propietarios de remolques.",
    subTitle: "Desde el taller",
    title: "Guías prácticas y tutoriales.",
    description: "Lo que le diríamos a un cliente en persona, escrito aquí. Guías de compra, pasos para reparaciones y el mantenimiento que la mayoría de la gente pasa por alto.",
    searchLabel: "Buscar en el blog",
    searchPlaceholder: "Buscar publicaciones - intente 'maza', 'frenos eléctricos', 'remolque para botes'…",
    clearSearch: "Limpiar búsqueda",
    categories: { All: "Todos", "Axles & Suspension": "Ejes y Suspensión", "Service & Repair": "Servicio y Reparación", "Buyer Guides": "Guías de Compra", "Trailer Parts": "Piezas para Remolque" },
    noPosts: (query: string, active: string) => `Ninguna publicación coincide con "${query}"${active !== "All" ? ` en ${active}` : ""}.`,
    clearFilters: "Limpiar filtros",
    readMore: "Leer más",
    showing: (shown: number, total: number) => `Mostrando ${shown} de ${total} publicaciones`,
    showMore: (n: number) => `Mostrar ${n} más`,
    showAll: (n: number) => `Mostrar todas las ${n}`
  }
};

const PAGE_SIZE = 6;

export default function BlogIndex() {
  const { locale } = useLocale();
  const t = useCopy(COPY);
  const [active, setActive] = useState<(typeof CATEGORIES)[number]>("All");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const trail = t.trail;
  const q = query.trim().toLowerCase();
  const filtered = getPosts(locale).filter((p) => {
    if (active !== "All" && p.category !== active) return false;
    if (!q) return true;
    return p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
  });
  const shown = filtered.slice(0, visible);
  const remaining = filtered.length - shown.length;
  useIdlePrefetchPosts(shown.map((p) => p.slug), 3, locale);
  const onCategoryChange = (c: (typeof CATEGORIES)[number]) => { setActive(c); setVisible(PAGE_SIZE); };
  const onQueryChange = (v: string) => { setQuery(v); setVisible(PAGE_SIZE); };

  return (
    <PageLayout>
      <Seo title={t.seoTitle} description={t.seoDesc} path="/blog/" jsonLd={[organizationJsonLd(), breadcrumbJsonLd(trail), blogJsonLd({ name: t.blogName, description: t.blogDesc, path: "/blog/", posts: getPosts(locale).map((p) => ({ title: p.title, slug: p.slug, date: p.dateIso, excerpt: p.excerpt })) })]} />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">{t.subTitle}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">{t.title}</h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">{t.description}</p>
        </div>
      </section>
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="mb-6">
          <label htmlFor="blog-search" className="sr-only">{t.searchLabel}</label>
          <div className="relative max-w-xl">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input id="blog-search" type="search" value={query} onChange={(e) => onQueryChange(e.target.value)} placeholder={t.searchPlaceholder} className="w-full pl-10 pr-10 py-3 rounded-md border border-border bg-background text-sm placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors" />
            {query && (<button type="button" onClick={() => onQueryChange("")} aria-label={t.clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><X className="h-4 w-4" /></button>)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (<button key={c} onClick={() => onCategoryChange(c)} className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${active === c ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:border-primary"}`}>{t.categories[c]}</button>))}
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.length === 0 && (
            <div className="md:col-span-2 lg:col-span-3 text-center py-16 border border-dashed border-border rounded-md">
              <p className="text-muted-foreground">{t.noPosts(query, active !== "All" ? t.categories[active] : "All")}</p>
              <button onClick={() => { setQuery(""); setActive("All"); setVisible(PAGE_SIZE); }} className="mt-3 text-sm text-primary-strong hover:underline underline-offset-4">{t.clearFilters}</button>
            </div>
          )}
          {shown.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}/`} {...postPrefetchHandlers(p.slug, locale)} className="group block bg-card rounded-md overflow-hidden border border-border hover:border-primary hover:shadow-md transition-all">
              <div className="h-48 relative bg-secondary overflow-hidden">
                <img src={assetUrl(postImage(p.slug, locale))} alt="" loading="lazy" width={1200} height={630} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 flex items-end p-5"><span className="text-[11px] uppercase tracking-[0.18em] text-white font-semibold bg-secondary/85 backdrop-blur-sm px-2 py-1 rounded">{t.categories[p.category]}</span></div>
              </div>
              <div className="p-5">
                <p className="text-xs text-muted-foreground mb-2">{locale === "es" ? new Intl.DateTimeFormat("es-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }).format(new Date(p.dateIso)) : p.date}</p>
                <h2 className="font-semibold leading-snug mb-2 group-hover:text-primary transition-colors">{p.title}</h2>
                <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">{p.excerpt}</p>
                <span className="mt-4 inline-flex items-center text-sm font-medium text-primary-strong">{t.readMore} <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </div>
            </Link>
          ))}
        </div>
        {remaining > 0 && (
          <div className="mt-12 flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">{t.showing(shown.length, filtered.length)}</p>
            <button onClick={() => setVisible((v) => v + PAGE_SIZE)} className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors">{t.showMore(Math.min(PAGE_SIZE, remaining))} <ArrowRight className="h-4 w-4" /></button>
            {remaining > PAGE_SIZE && (<button onClick={() => setVisible(filtered.length)} className="text-sm text-muted-foreground hover:text-primary underline underline-offset-4">{t.showAll(filtered.length)}</button>)}
          </div>
        )}
      </section>
    </PageLayout>
  );
}
